import { useEffect, useMemo, useState } from 'react';
import {
    isProductionCalculationReady,
    PRODUCTION_CALCULATION_TYPES,
    SHADE_CALCULATIONS,
    siteCalculationState,
} from '../services/siteCalculationState';
import { CalculationType } from '../@types/enums';
import { useViewerStore } from '../store/store';
import { logger } from '../services/loggingService';
import {
    beginFinalizeModuleGraph,
    endFinalizeModuleGraph,
} from '../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/TreeImpactEligibility';

export const EXPORT_CALC_WAIT_MS = 120_000;

export async function waitWhileCalculations(
    predicate: () => boolean,
    maxMs = EXPORT_CALC_WAIT_MS,
    timeoutLogMessage = 'Calculation prep timed out waiting for calculations',
): Promise<void> {
    const started = Date.now();
    while (predicate()) {
        if (Date.now() - started > maxMs) {
            logger.warn(timeoutLogMessage);
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
}

/** Runs shade recalc and waits for the production calculation chain before export. */
export async function runShadeAndProductionCalculationPrep(): Promise<void> {
    beginFinalizeModuleGraph();
    try {
        siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
        await siteCalculationState.useCalculations(SHADE_CALCULATIONS);
        await waitWhileCalculations(() => useViewerStore.getState().refreshingShade);
        await waitWhileCalculations(
            () =>
                PRODUCTION_CALCULATION_TYPES.some((type) =>
                    siteCalculationState.doesNeedUpdateOrUpdating(type),
                ) || siteCalculationState.doesNeedUpdateOrUpdating(SHADE_CALCULATIONS),
        );
    } finally {
        endFinalizeModuleGraph();
    }
}

function isAnyProductionCalculationPending(): boolean {
    return PRODUCTION_CALCULATION_TYPES.some((name) =>
        siteCalculationState.doesNeedUpdateOrUpdating(name),
    );
}

const PRODUCTION_STEP_LABELS: readonly { type: CalculationType; label: string }[] = [
    { type: CalculationType.ACCESS_MULTI, label: 'Loading Access Multipliers…' },
    { type: CalculationType.IDEAL_MULTI, label: 'Loading Ideal Multipliers…' },
    { type: CalculationType.MODULE_GRAPH, label: 'Calculating production values…' },
] as const;

export type SiteCalculationBlockedStatusInput = {
    loadingCompleted: boolean;
    refreshingShade: boolean;
    productionValuesReady: boolean;
    shadeCalculatingIdx?: number;
    shadeCalculatingLength?: number;
};

export type RunAiBlockedStatusInput = SiteCalculationBlockedStatusInput & {
    runAiReady: boolean;
    aiLoading: boolean;
};

type CalculationOption = {
    useCalculation?: boolean,
}

function readNeedsUpdateFlags(names: readonly CalculationType[]) {
    return names.reduce<{ [value in CalculationType]?: boolean }>((p, c) => {
        p[c] = siteCalculationState.doesNeedUpdateOrUpdating(c);
        return p;
    }, {});
}

function needsUpdateFlagsEqual(
    prev: { [value in CalculationType]?: boolean },
    next: { [value in CalculationType]?: boolean },
    names: readonly CalculationType[],
) {
    return names.every((name) => prev[name] === next[name]);
}

function calcNamesKey(names: readonly CalculationType[]) {
    return names.join('\0');
}

export function useCalculationStates(names: CalculationType[], options?: CalculationOption) {
    const namesKey = calcNamesKey(names);

    const [needsUpdate, setNeedsUpdate] = useState<{ [value in CalculationType]?: boolean }>(() =>
        readNeedsUpdateFlags(names),
    );

    useEffect(() => {
        const syncName = (name: CalculationType) => {
            const next = siteCalculationState.doesNeedUpdateOrUpdating(name);
            setNeedsUpdate((state) => (state[name] === next ? state : { ...state, [name]: next }));
        };

        const listener = (e: any) => syncName(e.detail.name as CalculationType);

        for (const name of names) {
            siteCalculationState.addNeedsUpdateListener(name, listener);
            siteCalculationState.addUpdatedListener(name, listener);
        }

        setNeedsUpdate((prev) => {
            const next = readNeedsUpdateFlags(names);
            return needsUpdateFlagsEqual(prev, next, names) ? prev : next;
        });

        return () => {
            for (const name of names) {
                siteCalculationState.removeNeedsUpdateListener(name, listener);
                siteCalculationState.removeUpdatedListener(name, listener);
            }
        };
    }, [namesKey]);

    if (options?.useCalculation) {
        siteCalculationState.useCalculations(...names);
    }

    return {
        needsUpdate,
    };
}

export function useCalculationState(name: CalculationType, options?: CalculationOption) {
    const {needsUpdate} = useCalculationStates([name], options);

    return {
        needsUpdate: needsUpdate[name],
    };
}

/** Human-readable label for the production calc step currently pending or in flight. */
export function getActiveProductionCalculationLabel(): string | null {
    for (const { type, label } of PRODUCTION_STEP_LABELS) {
        if (siteCalculationState.doesNeedUpdateOrUpdating(type)) return label;
    }
    return null;
}

function resolveSiteCalculationBlockedMessage(
    input: SiteCalculationBlockedStatusInput,
    needsUpdate: { [value in CalculationType]?: boolean },
): string | null {
    if (!input.loadingCompleted) return 'Loading site…';
    if (input.refreshingShade) {
        const { shadeCalculatingIdx: idx, shadeCalculatingLength: len } = input;
        if (typeof idx === 'number' && typeof len === 'number' && len > 0) {
            return `Calculating shade (${idx} of ${len})…`;
        }
        return 'Calculating shade…';
    }
    for (const { type, label } of PRODUCTION_STEP_LABELS) {
        if (needsUpdate[type] || siteCalculationState.doesNeedUpdateOrUpdating(type)) {
            return label;
        }
    }
    if (!input.productionValuesReady) return 'Preparing production values…';
    return null;
}

/** Status while site load, shade, or production calculations are still in progress. */
export function useSiteCalculationBlockedStatus(input: SiteCalculationBlockedStatusInput): string | null {
    const { needsUpdate } = useCalculationStates(PRODUCTION_CALCULATION_TYPES);

    return useMemo(
        () => resolveSiteCalculationBlockedMessage(input, needsUpdate),
        [
            input.loadingCompleted,
            input.refreshingShade,
            input.productionValuesReady,
            input.shadeCalculatingIdx,
            input.shadeCalculatingLength,
            needsUpdate,
        ],
    );
}

/**
 * Status message to show while Run AI is disabled (site load, shade, or production chain).
 * Returns null when Run AI is ready or actively running.
 */
export function useRunAiBlockedStatus(input: RunAiBlockedStatusInput): string | null {
    const siteStatus = useSiteCalculationBlockedStatus(input);

    return useMemo(() => {
        if (input.runAiReady || input.aiLoading) return null;
        return siteStatus;
    }, [input.runAiReady, input.aiLoading, siteStatus]);
}

/**
 * True once production numbers are in the store and the PV Watts calc chain is idle.
 * Listeners attach only while waiting for calcs — no polling, no perpetual subscriptions.
 */
export function useProductionCalculationReady() {
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const actualWattage = useViewerStore((s) => s.actualWattage);
    const modeCount = useViewerStore((s) => s.modeCount);

    const hasProductionNumbers = useMemo(
        () => Number(actualWattage) > 0 || Number(modeCount) > 0,
        [actualWattage, modeCount],
    );

    const awaitingCalcs = Boolean(loadingCompleted && !refreshingShade && hasProductionNumbers);

    const [calcTick, setCalcTick] = useState(0);

    const ready = useMemo(() => {
        if (!awaitingCalcs) return false;
        return isProductionCalculationReady();
    }, [awaitingCalcs, calcTick]);

    useEffect(() => {
        if (!awaitingCalcs || ready) return;

        const bump = () => setCalcTick((n) => n + 1);

        for (const name of PRODUCTION_CALCULATION_TYPES) {
            siteCalculationState.addNeedsUpdateListener(name, bump);
            siteCalculationState.addUpdatedListener(name, bump);
        }

        if (isProductionCalculationReady()) {
            bump();
        }

        return () => {
            for (const name of PRODUCTION_CALCULATION_TYPES) {
                siteCalculationState.removeNeedsUpdateListener(name, bump);
                siteCalculationState.removeUpdatedListener(name, bump);
            }
        };
    }, [awaitingCalcs, ready]);

    return ready;
}

function useProductionCalcPendingState() {
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const [calcTick, setCalcTick] = useState(0);

    const calcPending = useMemo(() => {
        void calcTick;
        return isAnyProductionCalculationPending();
    }, [calcTick]);

    useEffect(() => {
        const bump = () => setCalcTick((n) => n + 1);

        for (const name of PRODUCTION_CALCULATION_TYPES) {
            siteCalculationState.addNeedsUpdateListener(name, bump);
            siteCalculationState.addUpdatedListener(name, bump);
        }
        bump();

        return () => {
            for (const name of PRODUCTION_CALCULATION_TYPES) {
                siteCalculationState.removeNeedsUpdateListener(name, bump);
                siteCalculationState.removeUpdatedListener(name, bump);
            }
        };
    }, []);

    useEffect(() => {
        if (!loadingCompleted || !calcPending) return;

        void siteCalculationState.useCalculations(...PRODUCTION_CALCULATION_TYPES);
    }, [loadingCompleted, calcPending]);

    return calcPending;
}

/**
 * Loading gate for production panels (efficiency, roof table, monthly estimate).
 * Uses live calculation state (not only React-synced flags) and does not block on
 * stale pending flags once production numbers are already in the store — e.g. after finalize.
 */
export function useProductionPanelLoading(): boolean {
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const modeCount = useViewerStore((s) => s.modeCount);
    const actualWattage = useViewerStore((s) => s.actualWattage);
    const utilityWattage = useViewerStore((s) => s.utilityWattage);
    const calcPending = useProductionCalcPendingState();
    const productionValuesReady = useProductionCalculationReady();

    const hasModules = Number(modeCount) > 0;
    const hasStoredProductionValues =
        Number(actualWattage) > 0 || Number(utilityWattage) > 0;

    if (!loadingCompleted) return true;

    if (!hasModules) {
        return refreshingShade || calcPending;
    }

    if (hasStoredProductionValues) {
        return false;
    }

    return refreshingShade || calcPending || !productionValuesReady;
}

/** Status text while shade or production values are still being calculated. */
export function useProductionPanelLoadingMessage(): string | null {
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((s) => s.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);
    const productionValuesReady = useProductionCalculationReady();
    const isPending = useProductionPanelLoading();

    return useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady: productionValuesReady || !isPending,
        shadeCalculatingIdx: shadeCalculatingIdx + 1,
        shadeCalculatingLength,
    });
}

/**
 * Prepares shade and production calculations once before site report / .gen export.
 * Avoids calling useCalculations on every render (which can leave the popup stuck).
 */
export function useExportCalculationPrep() {
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((s) => s.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);

    const [prepReady, setPrepReady] = useState(false);

    const blockedMessage = useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady: prepReady,
        shadeCalculatingIdx: shadeCalculatingIdx + 1,
        shadeCalculatingLength,
    });

    useEffect(() => {
        if (!loadingCompleted) return;

        let cancelled = false;

        const runPrep = async (): Promise<void> => {
            try {
                await runShadeAndProductionCalculationPrep();
            } catch (err) {
                logger.error(err, 'Export calculation prep failed');
            } finally {
                if (!cancelled) setPrepReady(true);
            }
        };

        setPrepReady(false);
        void runPrep();

        return () => {
            cancelled = true;
            endFinalizeModuleGraph();
        };
    }, [loadingCompleted]);

    return {
        prepReady,
        blockedMessage,
        isBlocked: !prepReady || refreshingShade,
    };
}
